import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, MessageCircle, ArrowRight, X } from "lucide-react";
import { searchArticles, articleUrl, ARTICLE_GROUPS } from "@/lib/seoArticles";
import { useAllArticles } from "@/lib/useAllArticles";
import { api } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";

/* Universal "Search Memory Lane" component
 *
 * Sits on the landing hero (and anywhere else we want it). Searches the
 * standalone article corpus locally (fast, no backend round-trip) and
 * always offers Lane AI as the fallback when caregivers type a question
 * that doesn't map cleanly to a published article.
 *
 * Why client-side search:
 *   - The article corpus is small (≤ a few hundred items expected this
 *     year). Bringing fuse.js or hitting an API is unnecessary overhead.
 *   - Results render instantly as the caregiver types, which feels more
 *     like an assistant than a search box.
 *   - Lane AI fallback runs through the same /chat flow as everything
 *     else, so the seed-prompt + memory + auth-redirect plumbing all
 *     just work.
 */

const EXAMPLE_QUERIES = [
    "Dad won't shower",
    "Do I qualify for GUIDE?",
    "Why is mom wandering?",
    "When is hospice appropriate?",
    "VA dementia benefits",
    "Memory care costs",
    "Sleeping all day",
];

export default function SearchMemoryLane({ testid = "search-memory-lane" }) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const wrapRef = useRef(null);
    const [exampleIdx, setExampleIdx] = useState(0);

    // Rotate the placeholder example every 3s when input is empty
    useEffect(() => {
        if (query) return;
        const t = setInterval(() => setExampleIdx((i) => (i + 1) % EXAMPLE_QUERIES.length), 3000);
        return () => clearInterval(t);
    }, [query]);

    // Click-outside closes the dropdown
    useEffect(() => {
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const allArticles = useAllArticles();
    const results = useMemo(() => (query ? searchArticles(query, 8, allArticles) : []), [query, allArticles]);

    // Debounced GA + backend telemetry: fire once the caregiver has paused
    // typing for ~600ms so each keystroke isn't a separate event. We also
    // ping /api/admin/track/search when the query produces zero matches —
    // those misses surface in the Founder Command Center as content gaps.
    useEffect(() => {
        const q = query.trim();
        if (q.length < 3) return;
        const t = setTimeout(() => {
            track(EVENTS.SEARCH_MEMORY_LANE, {
                search_term: q,
                results_count: results.length,
                has_results: results.length > 0,
            });
            if (results.length === 0) {
                api.post("/admin/track/search", { query: q, ua: navigator.userAgent }).catch(() => { });
            }
        }, 600);
        return () => clearTimeout(t);
    }, [query, results.length]);

    const askLane = () => {
        const q = query.trim();
        if (!q) return;
        localStorage.setItem("ml_lane_seed_prompt", q);
        track(EVENTS.ASK_LANE_STARTED, { source: "search_memory_lane", seed: q.slice(0, 80) });
        navigate("/chat");
    };

    const goTo = (article) => {
        setOpen(false);
        navigate(articleUrl(article));
    };

    return (
        <div ref={wrapRef} className="relative w-full" data-testid={`${testid}-wrap`}>
            <div className="relative">
                <Search size={18} strokeWidth={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6E7A74] pointer-events-none" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            if (results.length > 0) goTo(results[0]);
                            else askLane();
                        } else if (e.key === "Escape") {
                            setOpen(false);
                            inputRef.current?.blur();
                        }
                    }}
                    placeholder={query ? "" : `Search Memory Lane — try "${EXAMPLE_QUERIES[exampleIdx]}"`}
                    data-testid={`${testid}-input`}
                    aria-label="Search Memory Lane"
                    className="w-full pl-11 pr-11 py-3.5 rounded-2xl border border-cream-dark bg-white text-base focus:outline-none focus:ring-2 focus:ring-teal-soft focus:border-teal-light"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => { setQuery(""); inputRef.current?.focus(); }}
                        data-testid={`${testid}-clear`}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-cream-light text-[#6E7A74]"
                        aria-label="Clear search"
                    >
                        <X size={14} strokeWidth={2} />
                    </button>
                )}
            </div>

            {open && query && (
                <div
                    data-testid={`${testid}-results`}
                    className="absolute left-0 right-0 top-full mt-2 z-30 rounded-2xl border border-cream-dark bg-white shadow-xl overflow-hidden max-h-[70vh] overflow-y-auto"
                >
                    {results.length > 0 ? (
                        <ul className="divide-y divide-cream-dark/60">
                            {results.map((r) => {
                                const gm = ARTICLE_GROUPS[r.group] || { label: r.group };
                                return (
                                    <li key={r.slug}>
                                        <button
                                            type="button"
                                            onClick={() => goTo(r)}
                                            data-testid={`${testid}-result-${r.slug}`}
                                            className="w-full text-left px-4 py-3 hover:bg-cream-light flex items-start gap-3 transition-colors"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[11px] uppercase tracking-wider font-semibold text-teal-dark">{gm.label}</p>
                                                <p className="mt-0.5 font-medium text-[#1F2624] leading-snug">{r.h1}</p>
                                                {r.summary && <p className="mt-0.5 text-xs text-[#4C5551] line-clamp-1">{r.summary}</p>}
                                            </div>
                                            <ArrowRight size={14} strokeWidth={1.8} className="text-[#6E7A74] mt-1 shrink-0" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <div className="px-4 py-5 text-sm text-[#4C5551]">
                            No matching articles. Lane can talk it through with you.
                        </div>
                    )}
                    {/* Ask-Lane fallback — always available */}
                    <button
                        type="button"
                        onClick={askLane}
                        data-testid={`${testid}-ask-lane`}
                        className="w-full text-left px-4 py-3.5 bg-teal-soft/40 hover:bg-teal-soft/60 border-t border-teal-light/40 flex items-center gap-3 transition-colors"
                    >
                        <span className="w-9 h-9 rounded-2xl bg-teal-dark text-white flex items-center justify-center shrink-0">
                            <MessageCircle size={16} strokeWidth={1.8} />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-teal-dark">Ask Lane: <span className="text-[#1F2624]">{query.length > 80 ? query.slice(0, 80) + "…" : query}</span></p>
                            <p className="text-xs text-[#4C5551] mt-0.5">Lane will remember your story and walk through it with you.</p>
                        </div>
                        <ArrowRight size={16} strokeWidth={1.8} className="text-teal-dark shrink-0" />
                    </button>
                </div>
            )}

            {/* Quiet example chips below the input — discoverability without
                shouting. */}
            {!query && (
                <ul className="mt-3 flex flex-wrap gap-1.5" data-testid={`${testid}-examples`}>
                    {EXAMPLE_QUERIES.slice(0, 5).map((ex) => (
                        <li key={ex}>
                            <button
                                type="button"
                                onClick={() => { setQuery(ex); setOpen(true); inputRef.current?.focus(); }}
                                data-testid={`${testid}-example-${ex.replace(/\W+/g, "-").toLowerCase()}`}
                                className="text-xs px-3 py-1 rounded-2xl bg-white border border-cream-dark text-[#4C5551] hover:border-teal-dark hover:text-teal-dark transition-colors"
                            >
                                {ex}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
